import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom'
import { Root } from './Root'
import { Home } from './pages/Home'
import { LoadingSpinner } from '@/shared/components/ui'
import { useAuth } from '@/shared/hooks/useAuth'
import type { UserRole } from '@/shared/lib/auth'

function ProtectedRoute({ minimum }: { minimum: UserRole }) {
  const { hasRole, loading } = useAuth()
  if (loading) return null
  if (!hasRole(minimum)) {
    return <Navigate to="/" replace />
  }
  return <Outlet />
}

// Lazy-load non-Home routes for code splitting
const Infrastructure = lazy(() => import('./pages/Infrastructure').then(m => ({ default: m.Infrastructure })))
const OrchestrationLayout = lazy(() => import('./features/orchestration/components/layout/OrchestrationLayout').then(m => ({ default: m.OrchestrationLayout })))
const CommandDeck = lazy(() => import('./features/orchestration/pages/CommandDeck').then(m => ({ default: m.CommandDeck })))
const TheForge = lazy(() => import('./features/orchestration/pages/TheForge').then(m => ({ default: m.TheForge })))
const TheLibrary = lazy(() => import('./features/orchestration/pages/TheLibrary').then(m => ({ default: m.TheLibrary })))
const OrchSettings = lazy(() => import('./features/orchestration/pages/OrchSettings').then(m => ({ default: m.OrchSettings })))
const Visibility = lazy(() => import('./features/orchestration/pages/Visibility').then(m => ({ default: m.Visibility })))
const PipelineEditor = lazy(() => import('./features/orchestration/pages/PipelineEditor').then(m => ({ default: m.PipelineEditor })))
const WorkflowTriage = lazy(() => import('./features/orchestration/pages/TodoTriage').then(m => ({ default: m.WorkflowTriage })))
const GitStatus = lazy(() => import('./features/orchestration/pages/GitStatus').then(m => ({ default: m.GitStatus })))
const LearningLayout = lazy(() => import('./features/learning/components/layout/LearningLayout').then(m => ({ default: m.LearningLayout })))
const DeckLibrary = lazy(() => import('./features/learning/pages/DeckLibrary').then(m => ({ default: m.DeckLibrary })))
const DeckDetail = lazy(() => import('./features/learning/pages/DeckDetail').then(m => ({ default: m.DeckDetail })))
const StudySessionPage = lazy(() => import('./features/learning/pages/StudySession').then(m => ({ default: m.StudySessionPage })))
const LearningStats = lazy(() => import('./features/learning/pages/LearningStats').then(m => ({ default: m.LearningStats })))
const CoursesPlaceholder = lazy(() => import('./features/learning/pages/CoursesPlaceholder').then(m => ({ default: m.CoursesPlaceholder })))
const PresentationLibrary = lazy(() => import('./features/learning/pages/PresentationLibrary').then(m => ({ default: m.PresentationLibrary })))
const PresentationViewer = lazy(() => import('./features/learning/pages/PresentationViewer').then(m => ({ default: m.PresentationViewer })))
const NewsPage = lazy(() => import('./features/news').then(m => ({ default: m.NewsPage })))
const SignalsPage = lazy(() => import('./features/news').then(m => ({ default: m.SignalsPage })))
const SavedPage = lazy(() => import('./features/news').then(m => ({ default: m.SavedPage })))
const HistoryPage = lazy(() => import('./features/news').then(m => ({ default: m.HistoryPage })))
const HistorySnapshotPage = lazy(() => import('./features/news').then(m => ({ default: m.HistorySnapshotPage })))
const NewsSettingsPage = lazy(() => import('./features/news').then(m => ({ default: m.NewsSettingsPage })))
const HtmlViewer = lazy(() => import('./features/orchestration/pages/HtmlViewer').then(m => ({ default: m.HtmlViewer })))
const MarkdownDocsPage = lazy(() => import('./pages/MarkdownDocsPage').then(m => ({ default: m.MarkdownDocsPage })))
const DevPreview = lazy(() => import('./pages/DevPreview').then(m => ({ default: m.DevPreview })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const DayActivityPage = lazy(() => import('./features/dashboard/pages/DayActivityPage').then(m => ({ default: m.DayActivityPage })))
const NotesLayout = lazy(() => import('./features/notes/components/layout/NotesLayout').then(m => ({ default: m.NotesLayout })))
const NotesListPage = lazy(() => import('./features/notes/pages/NotesListPage').then(m => ({ default: m.NotesListPage })))
const NoteEditorPage = lazy(() => import('./features/notes/pages/NoteEditorPage').then(m => ({ default: m.NoteEditorPage })))
const ProjectDetail = lazy(() => import('./features/projects/pages/ProjectDetail').then(m => ({ default: m.ProjectDetail })))
const ProjectsPage = lazy(() => import('./features/projects/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const TasksPage = lazy(() => import('./features/tasks/pages/TasksPage').then(m => ({ default: m.TasksPage })))

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-full"><LoadingSpinner /></div>}>
      {children}
    </Suspense>
  )
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'orchestration',
        element: <ProtectedRoute minimum="dev" />,
        children: [
          {
            path: '',
            element: <SuspenseWrapper><OrchestrationLayout /></SuspenseWrapper>,
            children: [
              { index: true, element: <SuspenseWrapper><CommandDeck /></SuspenseWrapper> },
              { path: 'deck', element: <SuspenseWrapper><CommandDeck /></SuspenseWrapper> },
              { path: 'visibility', element: <SuspenseWrapper><Visibility /></SuspenseWrapper> },
              { path: 'forge', element: <SuspenseWrapper><TheForge /></SuspenseWrapper> },
              { path: 'library', element: <SuspenseWrapper><TheLibrary /></SuspenseWrapper> },
              { path: 'pipeline-editor', element: <SuspenseWrapper><PipelineEditor /></SuspenseWrapper> },
              { path: 'workflow-triage', element: <SuspenseWrapper><WorkflowTriage /></SuspenseWrapper> },
              { path: 'git-status', element: <SuspenseWrapper><GitStatus /></SuspenseWrapper> },
              { path: 'settings', element: <SuspenseWrapper><OrchSettings /></SuspenseWrapper> },
              { path: 'docs', element: <SuspenseWrapper><MarkdownDocsPage /></SuspenseWrapper> },
              { path: 'reports', element: <SuspenseWrapper><HtmlViewer /></SuspenseWrapper> },
            ],
          },
        ],
      },
      {
        path: 'learning',
        element: <SuspenseWrapper><LearningLayout /></SuspenseWrapper>,
        children: [
          { index: true, element: <SuspenseWrapper><DeckLibrary /></SuspenseWrapper> },
          { path: 'deck/:deckId', element: <SuspenseWrapper><DeckDetail /></SuspenseWrapper> },
          { path: 'study/:deckId', element: <SuspenseWrapper><StudySessionPage /></SuspenseWrapper> },
          { path: 'presentations', element: <SuspenseWrapper><PresentationLibrary /></SuspenseWrapper> },
          { path: 'presentations/:id/present', element: <SuspenseWrapper><PresentationViewer /></SuspenseWrapper> },
          { path: 'stats', element: <SuspenseWrapper><LearningStats /></SuspenseWrapper> },
          { path: 'courses', element: <SuspenseWrapper><CoursesPlaceholder /></SuspenseWrapper> },
        ],
      },
      {
        path: 'news',
        children: [
          { index: true, element: <SuspenseWrapper><NewsPage /></SuspenseWrapper> },
          { path: 'signals', element: <SuspenseWrapper><SignalsPage /></SuspenseWrapper> },
          { path: 'saved', element: <SuspenseWrapper><SavedPage /></SuspenseWrapper> },
          { path: 'history', element: <SuspenseWrapper><HistoryPage /></SuspenseWrapper> },
          { path: 'history/:briefingId', element: <SuspenseWrapper><HistorySnapshotPage /></SuspenseWrapper> },
          { path: 'settings', element: <SuspenseWrapper><NewsSettingsPage /></SuspenseWrapper> },
        ],
      },
      {
        path: 'notes',
        element: <SuspenseWrapper><NotesLayout /></SuspenseWrapper>,
        children: [
          { index: true, element: <SuspenseWrapper><NotesListPage /></SuspenseWrapper> },
          { path: ':noteId', element: <SuspenseWrapper><NoteEditorPage /></SuspenseWrapper> },
        ],
      },
      {
        path: 'infrastructure',
        element: <ProtectedRoute minimum="dev" />,
        children: [
          {
            path: '',
            element: <SuspenseWrapper><Infrastructure /></SuspenseWrapper>,
          },
        ],
      },
      {
        path: 'day/:date',
        element: <SuspenseWrapper><DayActivityPage /></SuspenseWrapper>,
      },
      {
        path: 'tasks',
        element: <SuspenseWrapper><TasksPage /></SuspenseWrapper>,
      },
      {
        path: 'projects',
        element: <SuspenseWrapper><ProjectsPage /></SuspenseWrapper>,
      },
      {
        path: 'projects/:id',
        element: <SuspenseWrapper><ProjectDetail /></SuspenseWrapper>,
      },
      {
        path: 'dev-preview',
        element: <SuspenseWrapper><DevPreview /></SuspenseWrapper>,
      },
      {
        path: 'settings',
        element: <SuspenseWrapper><Settings /></SuspenseWrapper>,
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
